import { Component } from '@theme/component';
import { mediaQueryLarge, prefersReducedMotion } from '@theme/utilities';

/**
 * @typedef {Object} ApPdpHeroNavCursorConfig
 * @property {string} iconHtml
 * @property {string} circleColor
 * @property {string} iconColor
 * @property {number} size
 * @property {number} speed
 */

/**
 * @typedef {HTMLElement & {
 *   select: (input: number, event?: Event, options?: { animate?: boolean }) => unknown,
 *   next: (event?: Event, options?: { animate?: boolean }) => unknown,
 *   previous: (event?: Event, options?: { animate?: boolean }) => unknown,
 *   current: number,
 *   infinite: boolean,
 * }} ApPdpHeroSlideshow
 */

const FILMSTRIP_CLONE_CAP = 24;

/**
 * PDP hero media host: hover circle-with-arrow, a one-image step for
 * variable-width filmstrip slides (Horizon `next()` skips by visible count),
 * and clone-until-snappable looping when the strip is too short to bring
 * every image to the start (ticket 42814, two-image filmstrip).
 *
 * @extends {Component<Record<string, never>>}
 */
class ApPdpHeroNavCursor extends Component {
  /** @type {HTMLElement | null} */
  #cursor = null;

  /** @type {number} */
  #posX = 0;

  /** @type {number} */
  #posY = 0;

  /** @type {number} */
  #targetX = 0;

  /** @type {number} */
  #targetY = 0;

  /** @type {number} */
  #raf = 0;

  /** @type {ApPdpHeroNavCursorConfig | null} */
  #config = null;

  /** @type {MutationObserver | null} */
  #observer = null;

  /** @type {ResizeObserver | null} */
  #filmstripResizeObserver = null;

  /** @type {number} */
  #filmstripRaf = 0;

  /** @type {boolean} */
  #fillingFilmstrip = false;

  connectedCallback() {
    super.connectedCallback();

    this.#patchSlideshows();
    customElements.whenDefined('slideshow-component').then(() => {
      if (!this.isConnected) return;
      this.#patchSlideshows();
      this.#syncFilmstripLoops();
    });

    this.#observer = new MutationObserver((records) => {
      if (this.#fillingFilmstrip) return;
      if (!this.#shouldRebuildFilmstrip(records)) return;
      this.#patchSlideshows();
      this.#syncFilmstripLoops(true);
    });
    this.#observeFilmstripMutations();

    this.#filmstripResizeObserver = new ResizeObserver(() => {
      if (this.#fillingFilmstrip) return;
      if (this.#filmstripRaf) cancelAnimationFrame(this.#filmstripRaf);
      this.#filmstripRaf = requestAnimationFrame(() => {
        this.#filmstripRaf = 0;
        this.#syncFilmstripLoops();
      });
    });
    this.#filmstripResizeObserver.observe(this);

    mediaQueryLarge.addEventListener('change', this.#onBreakpointChange);
    this.#syncFilmstripLoops();

    if (!this.hasAttribute('data-nav-cursor')) return;

    this.#config = this.#readConfig();
    if (!this.#config) return;

    this.#cursor = this.#createCursor(this.#config);
    document.body.append(this.#cursor);

    this.addEventListener('pointerenter', this.#onPointerEnter);
    this.addEventListener('pointerleave', this.#onPointerLeave);
    this.addEventListener('pointermove', this.#onPointerMove);
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#observer?.disconnect();
    this.#observer = null;

    this.#filmstripResizeObserver?.disconnect();
    this.#filmstripResizeObserver = null;

    if (this.#filmstripRaf) cancelAnimationFrame(this.#filmstripRaf);
    this.#filmstripRaf = 0;

    mediaQueryLarge.removeEventListener('change', this.#onBreakpointChange);

    this.removeEventListener('pointerenter', this.#onPointerEnter);
    this.removeEventListener('pointerleave', this.#onPointerLeave);
    this.removeEventListener('pointermove', this.#onPointerMove);
    this.removeEventListener('click', this.#onClick);

    if (this.#raf) cancelAnimationFrame(this.#raf);
    this.#raf = 0;
    this.#cursor?.remove();
    this.#cursor = null;
  }

  /**
   * Horizon `next()`/`previous()` skip by `visibleSlides.length` when more than
   * one slide is on screen. Filmstrip portraits would jump several images.
   * Always step one slide (the width of that image).
   */
  #patchSlideshows() {
    const slideshows = this.querySelectorAll('slideshow-component');

    for (const slideshow of slideshows) {
      if (!(slideshow instanceof HTMLElement)) continue;
      if (slideshow.dataset.apStepPatched === 'true') continue;

      const element = /** @type {ApPdpHeroSlideshow} */ (slideshow);
      if (typeof element.select !== 'function') continue;

      slideshow.dataset.apStepPatched = 'true';

      element.next = (event, options) => {
        element.select((element.current ?? 0) + 1, event, options);
      };

      element.previous = (event, options) => {
        element.select((element.current ?? 0) - 1, event, options);
      };
    }
  }

  #onBreakpointChange = () => {
    this.#syncFilmstripLoops();
  };

  /**
   * Duplicate filmstrip slides until every original can snap to the start,
   * so a 2-image strip can still feel like a slider (Laura / ticket 42814).
   *
   * @param {boolean} [force]
   */
  #syncFilmstripLoops(force = false) {
    if (this.#fillingFilmstrip) return;

    if (!mediaQueryLarge.matches) {
      this.#clearFilmstripClones();
      return;
    }

    const slideshows = this.querySelectorAll(
      '.ap-pdp-hero__slideshow--desktop, .ap-pdp-hero__slideshow--shared'
    );

    for (const slideshow of slideshows) {
      if (!(slideshow instanceof HTMLElement)) continue;
      this.#fillFilmstrip(slideshow, force);
    }
  }

  /**
   * @param {HTMLElement} slideshow
   * @param {boolean} [force]
   */
  #fillFilmstrip(slideshow, force = false) {
    const scroller = slideshow.querySelector('slideshow-slides');
    if (!(scroller instanceof HTMLElement)) return;
    if (getComputedStyle(slideshow).display === 'none') return;

    const originals = this.#originalSlides(scroller);
    if (!originals.length) return;

    const loop = this.#filmstripLoopMode();
    const single = this.#filmstripSingleMode();

    if (loop === 'never' || (originals.length === 1 && single === 'fill') || (loop === 'few' && originals.length >= 3)) {
      this.#withFilmstripMuted(() => this.#removeClones(scroller));
      return;
    }

    if (!force && this.#filmstripAlreadyFilled(scroller, originals, single)) return;

    if (originals.some((slide) => slide.getBoundingClientRect().width < 1)) return;

    this.#withFilmstripMuted(() => {
      this.#removeClones(scroller);

      let guard = 0;

      if (originals.length === 1 && single === 'repeat') {
        const only = originals[0];
        if (!only) return;

        while (scroller.scrollWidth < scroller.clientWidth + only.offsetWidth && guard < FILMSTRIP_CLONE_CAP) {
          this.#appendClone(scroller, only);
          guard += 1;
        }
      } else {
        while (!this.#canSnapOriginals(scroller, originals) && guard < FILMSTRIP_CLONE_CAP) {
          const source = originals[guard % originals.length];
          if (!source) break;
          this.#appendClone(scroller, source);
          guard += 1;
        }
      }

      this.#bindFilmstripModulo(slideshow, scroller);
    });
  }

  #observeFilmstripMutations() {
    this.#observer?.observe(this, { childList: true, subtree: true });
  }

  /**
   * Clone inserts must not re-enter fill. A MutationObserver with force=true
   * on those inserts froze the PDP (infinite add/remove).
   *
   * @param {() => void} callback
   */
  #withFilmstripMuted(callback) {
    this.#fillingFilmstrip = true;
    this.#observer?.disconnect();

    try {
      callback();
    } finally {
      this.#observer?.takeRecords();
      this.#fillingFilmstrip = false;
      if (this.isConnected) this.#observeFilmstripMutations();
      this.#observer?.takeRecords();
    }
  }

  /**
   * @param {MutationRecord[]} records
   * @returns {boolean}
   */
  #shouldRebuildFilmstrip(records) {
    for (const record of records) {
      if (record.target instanceof Element && record.target.closest('[data-ap-filmstrip-clone]')) {
        continue;
      }

      for (const node of [...record.addedNodes, ...record.removedNodes]) {
        if (!(node instanceof Element)) continue;
        if (node.hasAttribute('data-ap-filmstrip-clone') || node.closest('[data-ap-filmstrip-clone]')) continue;
        if (node.matches('slideshow-slide') && !node.getAttribute('slide-id')) continue;
        if (
          node.matches('slideshow-slide, slideshow-component, media-gallery') ||
          node.querySelector('slideshow-slide[slide-id], slideshow-component, media-gallery')
        ) {
          return true;
        }
      }
    }

    return false;
  }

  /**
   * @param {HTMLElement} scroller
   * @param {HTMLElement[]} originals
   * @param {'fill' | 'repeat'} single
   * @returns {boolean}
   */
  #filmstripAlreadyFilled(scroller, originals, single) {
    const cloneCount = scroller.querySelectorAll('[data-ap-filmstrip-clone]').length;
    if (!cloneCount) return false;
    if (cloneCount >= FILMSTRIP_CLONE_CAP) return true;

    if (originals.length === 1 && single === 'repeat') {
      const only = originals[0];
      return Boolean(only && scroller.scrollWidth >= scroller.clientWidth + only.offsetWidth - 1);
    }

    return this.#canSnapOriginals(scroller, originals);
  }

  /**
   * @returns {'always' | 'few' | 'never'}
   */
  #filmstripLoopMode() {
    const hero = this.closest('.ap-pdp-hero');
    const value = this.dataset.apFilmstripLoop || hero?.getAttribute('data-ap-filmstrip-loop') || 'always';

    if (value === 'few' || value === 'never') return value;
    return 'always';
  }

  /**
   * @returns {'fill' | 'repeat'}
   */
  #filmstripSingleMode() {
    const hero = this.closest('.ap-pdp-hero');
    const value =
      this.dataset.apFilmstripSingle || hero?.getAttribute('data-ap-filmstrip-single') || 'fill';

    return value === 'repeat' ? 'repeat' : 'fill';
  }

  /**
   * @param {HTMLElement} scroller
   * @returns {HTMLElement[]}
   */
  #originalSlides(scroller) {
    return /** @type {HTMLElement[]} */ (
      [...scroller.querySelectorAll('slideshow-slide')].filter(
        (slide) => !slide.hasAttribute('data-ap-filmstrip-clone') && !slide.hasAttribute('hidden')
      )
    );
  }

  /**
   * @param {HTMLElement} scroller
   * @param {HTMLElement[]} originals
   * @returns {boolean}
   */
  #canSnapOriginals(scroller, originals) {
    const scrollerRect = scroller.getBoundingClientRect();
    const maxScroll = scroller.scrollWidth - scroller.clientWidth;

    for (const slide of originals) {
      const slideRect = slide.getBoundingClientRect();
      const start = slideRect.left - scrollerRect.left + scroller.scrollLeft;
      if (start > maxScroll + 1) return false;
    }

    return true;
  }

  /**
   * @param {HTMLElement} scroller
   * @param {HTMLElement} source
   */
  #appendClone(scroller, source) {
    const clone = /** @type {HTMLElement} */ (source.cloneNode(true));
    clone.removeAttribute('ref');
    clone.removeAttribute('slide-id');
    clone.removeAttribute('reveal');
    clone.removeAttribute('id');
    clone.setAttribute('data-ap-filmstrip-clone', '');
    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('inert', '');

    for (const node of clone.querySelectorAll('[id]')) {
      node.removeAttribute('id');
    }

    for (const control of clone.querySelectorAll('button, a')) {
      control.setAttribute('tabindex', '-1');
    }

    scroller.append(clone);
  }

  /**
   * @param {HTMLElement} [root]
   */
  #clearFilmstripClones(root = this) {
    this.#withFilmstripMuted(() => {
      for (const clone of root.querySelectorAll('[data-ap-filmstrip-clone]')) {
        clone.remove();
      }
    });
  }

  /**
   * @param {HTMLElement} scroller
   */
  #removeClones(scroller) {
    for (const clone of scroller.querySelectorAll('[data-ap-filmstrip-clone]')) {
      clone.remove();
    }
  }

  /**
   * @param {HTMLElement} slideshow
   * @param {HTMLElement} scroller
   */
  #bindFilmstripModulo(slideshow, scroller) {
    if (slideshow.dataset.apFilmstripModulo === 'true') return;
    slideshow.dataset.apFilmstripModulo = 'true';

    scroller.addEventListener('scrollend', () => {
      requestAnimationFrame(() => this.#moduloFilmstrip(slideshow));
    });
  }

  /**
   * When the user drags into cloned slides, jump back by one original set
   * so the loop does not run out of strip.
   *
   * @param {HTMLElement} slideshow
   */
  #moduloFilmstrip(slideshow) {
    if (slideshow.hasAttribute('transitioning')) return;

    const scroller = slideshow.querySelector('slideshow-slides');
    if (!(scroller instanceof HTMLElement)) return;

    const originals = this.#originalSlides(scroller);
    if (originals.length < 1) return;

    const first = originals[0];
    const last = originals[originals.length - 1];
    if (!first || !last) return;

    const setWidth = last.offsetLeft + last.offsetWidth - first.offsetLeft;
    if (setWidth < 1) return;

    if (scroller.scrollLeft < setWidth - 1) return;

    scroller.scrollLeft -= Math.floor(scroller.scrollLeft / setWidth) * setWidth;
  }

  /**
   * @returns {ApPdpHeroNavCursorConfig | null}
   */
  #readConfig() {
    const script = this.querySelector('script[data-ap-hero-nav-cursor-config]');
    if (!(script instanceof HTMLScriptElement) || !script.textContent) return null;

    try {
      return /** @type {ApPdpHeroNavCursorConfig} */ (JSON.parse(script.textContent));
    } catch (error) {
      console.warn('[ap-pdp-hero-nav-cursor] Invalid config JSON', error);
      return null;
    }
  }

  /**
   * @param {ApPdpHeroNavCursorConfig} config
   * @returns {HTMLElement}
   */
  #createCursor(config) {
    const root = document.createElement('div');
    root.className = 'ap-pdp-hero-nav-cursor';
    root.setAttribute('aria-hidden', 'true');
    root.style.setProperty('--ap-hero-nav-cursor-size', `${config.size}px`);
    root.style.setProperty('--ap-hero-nav-cursor-color', config.circleColor);
    root.style.setProperty('--ap-hero-nav-cursor-icon-color', config.iconColor);

    const circle = document.createElement('div');
    circle.className = 'ap-pdp-hero-nav-cursor__circle';

    const icon = document.createElement('div');
    icon.className = 'ap-pdp-hero-nav-cursor__icon';
    icon.innerHTML = config.iconHtml;

    circle.append(icon);
    root.append(circle);
    return root;
  }

  /**
   * @returns {ApPdpHeroSlideshow | null}
   */
  #getSlideshow() {
    const desktop = this.querySelector('.ap-pdp-hero__slideshow--desktop');
    const mobile = this.querySelector('.ap-pdp-hero__slideshow--mobile');
    const shared = this.querySelector('.ap-pdp-hero__slideshow--shared');
    const fallback = this.querySelector('slideshow-component');

    let node = fallback;
    if (mediaQueryLarge.matches) {
      node = desktop || shared || fallback;
    } else {
      node = mobile || shared || fallback;
    }

    if (!(node instanceof HTMLElement) || typeof /** @type {ApPdpHeroSlideshow} */ (node).select !== 'function') {
      return null;
    }

    return /** @type {ApPdpHeroSlideshow} */ (node);
  }

  /**
   * @param {EventTarget | null} target
   * @returns {boolean}
   */
  #isInteractive(target) {
    if (!(target instanceof Element)) return false;

    return Boolean(target.closest('button, a, input, select, textarea, slideshow-controls'));
  }

  /**
   * @param {PointerEvent} event
   */
  #onPointerEnter = (event) => {
    if (event.pointerType !== 'mouse') return;
    this.#targetX = event.clientX;
    this.#targetY = event.clientY;
    this.#posX = event.clientX;
    this.#posY = event.clientY;
    this.#cursor?.classList.add('is-visible');
    if (!this.#raf) this.#tick();
  };

  #onPointerLeave = () => {
    this.#cursor?.classList.remove('is-visible');
    if (this.#raf) cancelAnimationFrame(this.#raf);
    this.#raf = 0;
  };

  /**
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    if (event.pointerType !== 'mouse') return;

    this.#targetX = event.clientX;
    this.#targetY = event.clientY;
    this.#updateDirection(event.clientX);

    if (this.#isInteractive(event.target) || !this.#canAdvance()) {
      this.#cursor?.classList.remove('is-visible');
      return;
    }

    this.#cursor?.classList.add('is-visible');
    if (!this.#raf) this.#tick();
  };

  /**
   * @param {MouseEvent} event
   */
  #onClick = (event) => {
    if (this.#isInteractive(event.target)) return;

    const slideshow = this.#getSlideshow();
    if (!slideshow) return;

    const rect = this.getBoundingClientRect();
    if (rect.width <= 0) return;

    const goingPrevious = event.clientX - rect.left < rect.width / 2;
    const index = (slideshow.current ?? 0) + (goingPrevious ? -1 : 1);

    slideshow.select(index, event);
  };

  /**
   * @param {number} clientX
   */
  #updateDirection(clientX) {
    const cursor = this.#cursor;
    if (!cursor) return;

    const rect = this.getBoundingClientRect();
    const goingPrevious = clientX - rect.left < rect.width / 2;
    cursor.dataset.direction = goingPrevious ? 'previous' : 'next';

    const slideshow = this.#getSlideshow();
    const infinite = Boolean(slideshow?.infinite);
    cursor.classList.toggle('is-boundary-previous', Boolean(!infinite && slideshow?.current === 0));
    cursor.classList.toggle(
      'is-boundary-next',
      Boolean(!infinite && slideshow && slideshow.current === this.#slideCount(slideshow) - 1)
    );
  }

  /**
   * @returns {boolean}
   */
  #canAdvance() {
    const slideshow = this.#getSlideshow();
    if (!slideshow) return false;

    const count = this.#slideCount(slideshow);
    if (count > 1) return true;

    return Boolean(slideshow.querySelector('[data-ap-filmstrip-clone]'));
  }

  /**
   * @param {ApPdpHeroSlideshow} slideshow
   * @returns {number}
   */
  #slideCount(slideshow) {
    return slideshow.querySelectorAll('slideshow-slide:not([hidden]):not([data-ap-filmstrip-clone])').length;
  }

  #tick = () => {
    const cursor = this.#cursor;
    if (!cursor?.classList.contains('is-visible')) {
      this.#raf = 0;
      return;
    }

    const speed = prefersReducedMotion() ? 1 : (this.#config?.speed ?? 0.2);
    this.#posX += (this.#targetX - this.#posX) * speed;
    this.#posY += (this.#targetY - this.#posY) * speed;
    cursor.style.transform = `translate3d(${this.#posX}px, ${this.#posY}px, 0)`;

    this.#raf = requestAnimationFrame(this.#tick);
  };
}

if (!customElements.get('ap-pdp-hero-nav-cursor')) {
  customElements.define('ap-pdp-hero-nav-cursor', ApPdpHeroNavCursor);
}
